import { useCallback, useRef, useState } from "react";

import { bytesPerMegaByte } from "@smart/bridge-types-basic";
import {
  FileFieldItem,
  FileInputField,
  FileUpload,
  Modal,
  UploadFileProps,
  UploadStatus,
} from "@smart/itops-sb-design-system-dom";
import {
  checkIsPdfEncrypted,
  checkIsWordDocEncrypted,
  unique,
} from "@smart/itops-utils-basic";

import { ErrorDisplay } from "./error";
import { Label } from "./label";
import { FieldProps, FieldWrapper } from "./wrapper";
import { fieldName } from "../../hooks";
import { LookupOptions } from "../../types";

const defaultMaxNumOfFiles = 5;
const defaultFileSizeLimitMegaByte = 128;

export type FileFieldValue = {
  fileName: string;
  downloadUrl: string;
  uploadStatus: UploadStatus;
  key?: string;
};

export const FileField = ({
  field,
  index,
  innerRef,
  value,
  error,
  loading,
  disabled,
  hideLabel,
  onChange,
  onBlur,
  fileLookup: { load, upload },
  uploadFileProps,
  maxNumOfFiles,
  fileSizeLimitMegaByte,
  supportedFileTypes,
}: FieldProps<HTMLInputElement, FileFieldValue[]> & {
  fileLookup: LookupOptions["fileLookup"];
  maxNumOfFiles?: number;
  fileSizeLimitMegaByte?: number;
  supportedFileTypes?: { name: string; extension: string }[];
} & { uploadFileProps: UploadFileProps }) => {
  const maxFilesModal = useState(false);
  const [exceedMaxFiles, setExceedMaxFiles] = useState(false);
  const maxNumOfFilesToUse = maxNumOfFiles || defaultMaxNumOfFiles;
  const fileSizeLimitToUse =
    fileSizeLimitMegaByte || defaultFileSizeLimitMegaByte;
  const buildSuportFileTypesInfo = () => {
    if (!supportedFileTypes?.length) return "";
    const suportedFileTypeNames = unique(supportedFileTypes.map((f) => f.name));
    const last = suportedFileTypeNames.pop() || "";
    const info =
      suportedFileTypeNames.length > 0
        ? `${suportedFileTypeNames.join(", ")} and ${last}; `
        : last;
    return `${info.charAt(0).toLocaleUpperCase()}${info.slice(1)}`;
  };
  const supportFileTypesInfo = buildSuportFileTypesInfo();
  const sizeLimit = `Max ${maxNumOfFilesToUse} files; ${supportFileTypesInfo}Max ${fileSizeLimitToUse} MB each.`;
  const acceptedFileTypes = supportedFileTypes?.length
    ? supportedFileTypes.map((f) => f.extension).join(",")
    : undefined;
  const invalidStatuses: UploadStatus[] = [
    "exceedSizeLimit",
    "failedToUpload",
    "unsupportedFileType",
  ];

  // We use a ref to keep the latest files instead of relaying on the value passed in
  // so we can update (e.g changing uploadStatus) in between rendering cycles and not get outdated data.
  const filesRef = useRef<
    (FileFieldValue & { file?: File; uploadUrl?: string })[]
  >(
    // For backwards compatibility
    (value as unknown as Omit<FileFieldValue, "uploadStatus">)?.downloadUrl
      ? [
          {
            ...(value as unknown as Omit<FileFieldValue, "uploadStatus">),
            uploadStatus: "uploaded",
          },
        ]
      : value || [],
  );

  const fieldWrapperRef = useRef<HTMLDivElement | null>(null);

  const scrollIntoFieldWrapper = () => {
    const fieldWrapperElement = fieldWrapperRef.current;
    if (!fieldWrapperElement) return;

    const headerHeight =
      document
        .querySelector('[data-testid="heading-wrapper"]')
        ?.getBoundingClientRect().height ?? 0;

    const scrollParent =
      document.querySelector('[data-testid="page-wrapper"]') ||
      document.scrollingElement ||
      document.documentElement;

    const offsetTopInParent =
      fieldWrapperElement.getBoundingClientRect().top -
      scrollParent.getBoundingClientRect().top +
      scrollParent.scrollTop;

    const target = Math.max(0, offsetTopInParent - headerHeight);

    scrollParent.scrollTo({ top: target, behavior: "smooth" });
  };

  const updateFieldValue = () =>
    onChange(
      filesRef.current.map(({ fileName, downloadUrl, uploadStatus, key }) => ({
        fileName,
        downloadUrl,
        uploadStatus,
        key,
      })),
    );

  const removeInvalidFiles = () => {
    filesRef.current = filesRef.current.filter(
      ({ uploadStatus }) => !invalidStatuses.includes(uploadStatus),
    );
  };

  const validateFile = async (
    file: File,
  ): Promise<UploadStatus | undefined> => {
    if (file.size > fileSizeLimitToUse * bytesPerMegaByte)
      return "exceedSizeLimit";

    const extension = file.name.split(".").pop() || "";
    if (
      acceptedFileTypes &&
      !acceptedFileTypes.includes(`.${extension.toLowerCase()}`)
    ) {
      return "unsupportedFileType";
    }

    const checkIsFileEncrypted = async () => {
      switch (extension?.toLowerCase()) {
        case "pdf":
          return checkIsPdfEncrypted(file);
        case "doc":
        case "docx":
          return checkIsWordDocEncrypted(file);
        default:
          return false;
      }
    };
    if (await checkIsFileEncrypted()) {
      return "fileEncrypted";
    }

    return undefined;
  };

  const addFiles = async (files: File[]) => {
    filesRef.current = [
      ...filesRef.current,
      ...(await Promise.all(
        files.map(async (file) => ({
          fileName: file.name,
          downloadUrl: "",
          uploadUrl: "",
          uploadStatus: (await validateFile(file)) || ("notUploaded" as const),
          file,
        })),
      )),
    ];
    updateFieldValue();
  };

  const id = fieldName({ field, index });
  const errorId = fieldName({ field, index, suffix: "error" });

  const onSelect = useCallback(
    async (fileList?: FileList | null) => {
      if (!fileList?.length) return;

      removeInvalidFiles();

      if (filesRef.current.length >= maxNumOfFilesToUse) {
        maxFilesModal[1](true);
        return;
      }
      const files = Array.from(fileList);
      const filesToAdd =
        filesRef.current.length + files.length > maxNumOfFilesToUse
          ? files.slice(0, maxNumOfFilesToUse - filesRef.current.length)
          : files;
      if (filesToAdd.length < files.length) setExceedMaxFiles(true);

      await addFiles(filesToAdd);

      let result: Awaited<ReturnType<typeof load>>;
      try {
        result = await load({
          fileNames: files.map((f) => f.name),
          fieldUri: field.uri,
        });
        scrollIntoFieldWrapper();
      } catch (e) {
        console.error(e);
      }
      filesRef.current = filesRef.current.map((fileValue, i) => {
        // We don't need to update existing files
        const offsetIndex = filesRef.current.length - filesToAdd.length;
        if (i < offsetIndex) return fileValue;

        const resultIndex: number = i - offsetIndex;
        return {
          ...fileValue,
          downloadUrl: result?.[resultIndex].downloadUrl || "",
          uploadUrl: result?.[resultIndex].uploadUrl || "",
          uploadStatus:
            !result?.[resultIndex].uploadUrl &&
            !invalidStatuses.includes(fileValue.uploadStatus)
              ? "failedToUpload"
              : fileValue.uploadStatus,
          key: result?.[resultIndex].key,
        };
      });

      updateFieldValue();
      onBlur();
    },
    [field.uri],
  );

  const getUploadedFiles = () => {
    if (!value) return null;
    const uploadedFiles = Array.isArray(value)
      ? value
      : [
          {
            ...(value as Omit<FileFieldValue, "uploadStatus">),
            uploadStatus: "uploaded" as const,
          },
        ];
    if (!uploadedFiles.length) return null;

    return (
      <div className="flex flex-col gap-small mt-[1.6rem]">
        {uploadedFiles.map((file, i) => (
          <FileFieldItem
            // eslint-disable-next-line react/no-array-index-key
            key={i}
            fileName={file.fileName}
            downloadUrl={file.downloadUrl}
            uploadUrl={filesRef.current[i]?.uploadUrl}
            file={filesRef.current[i]?.file}
            uploadStatus={file.uploadStatus}
            upload={upload}
            fileIndex={i}
            fieldId={id}
            uploadFileProps={uploadFileProps}
            fileSizeLimitMegaByte={fileSizeLimitToUse}
            maxNumOfFiles={maxNumOfFilesToUse}
            onStatusChange={(status: UploadStatus) => {
              filesRef.current = filesRef.current.map((f, fileIndex) =>
                i === fileIndex ? { ...f, uploadStatus: status } : f,
              );
              updateFieldValue();
              onBlur();
            }}
            onRemove={() => {
              const toUpdate = [...filesRef.current];
              toUpdate.splice(i, 1);
              filesRef.current = toUpdate;
              setExceedMaxFiles(false);
              updateFieldValue();
              onBlur();
            }}
          />
        ))}
      </div>
    );
  };

  return (
    <FieldWrapper disabled={disabled} ref={fieldWrapperRef} isLoading={loading}>
      <label htmlFor={id}>
        <Label {...field} hideLabel={hideLabel} />
      </label>
      <FileUpload
        className="mt-[0.8rem]"
        id={id}
        error={error}
        errorId={errorId}
        onSelect={onSelect}
        sizeLimit={sizeLimit}
        disabled={disabled}
      />
      <ErrorDisplay
        className="mt-[0.5rem]"
        uri={field.uri}
        index={index}
        error={
          error ||
          (exceedMaxFiles
            ? "Maximum upload limit reached. Only 5 files were added."
            : undefined)
        }
      />
      {getUploadedFiles()}
      <FileInputField
        className="file-value"
        ref={innerRef}
        id={id}
        name={id}
        aria-invalid={!!error}
        aria-errormessage={error ? errorId : undefined}
        type="file"
        data-testid="file-upload"
        multiple
        accept={acceptedFileTypes}
        onChange={async (e) => {
          e.preventDefault();
          onSelect(e.target.files).catch(console.error);
        }}
      />
      <Modal
        header={{
          text: "Upload Limit Reached",
          icon: "solidFileArrowUp",
          hideIconOnMobile: true,
        }}
        open={maxFilesModal[0]}
        onClose={() => maxFilesModal[1](false)}
        footer={{
          buttons: [
            {
              text: "Ok",
              type: "button",
              onClick: () => maxFilesModal[1](false),
            },
          ],
        }}
        closeOptions={{
          escapeKey: false,
          clickOutside: false,
        }}
      >
        <p>
          You can upload a maximum of 5 files. Please remove some to add new
          ones.
        </p>
      </Modal>
    </FieldWrapper>
  );
};
